import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { dashboard, messages } from '../../api/endpoints';
import { useAuthStore } from '../../stores/authStore';
import { useAppStore } from '../../stores/appStore';
import { company as companyApi } from '../../api/endpoints';
import {
  COLORS,
  FOOTER_HEIGHT,
  HEADER_HEIGHT,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_WIDTH,
} from './layoutConstants';
import TopNavBar from './TopNavBar';
import SidebarContent from './SidebarContent';
import { KeyboardShortcutsOverlay } from '../common/KeyboardShortcutsOverlay';
import WalkthroughOverlay from '../common/WalkthroughOverlay';

export default function AppLayout() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px

  // Zustand selectors: subscribe to individual slices to avoid unnecessary re-renders
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const setYears = useAppStore((s) => s.setYears);
  const yearsLoaded = useAppStore((s) => s.yearsLoaded);

  // Fetch company info to populate dynamic years from financial_periods
  const isCompanyScoped = user?.role === 'company_user' || user?.role === 'consultant_user';
  const { data: companyInfo } = useQuery({
    queryKey: ['company-info'],
    queryFn: companyApi.getInfo,
    enabled: !!user && isCompanyScoped && !yearsLoaded,
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (companyInfo?.financial_periods && !yearsLoaded) {
      const calcYears = companyInfo.financial_periods
        .filter((fp) => !fp.is_transition)
        .map((fp) => fp.year)
        .sort((a, b) => a - b);
      setYears(calcYears);
    }
  }, [companyInfo, yearsLoaded, setYears]);

  // Prefetch dashboard summary so it's warm in cache when user navigates to /dashboard
  const queryClient = useQueryClient();
  const selectedYear = useAppStore((s) => s.selectedYear);
  useEffect(() => {
    if (isCompanyScoped && selectedYear) {
      queryClient.prefetchQuery({
        queryKey: ['dashboard-summary', selectedYear],
        queryFn: () => dashboard.getSummary(selectedYear),
      });
    }
  }, [isCompanyScoped, selectedYear, queryClient]);

  // On mobile: drawerOpen controls the temporary drawer. Starts closed.
  // On desktop: drawerOpen controls expanded/collapsed sidebar. Starts expanded.
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const userMenuOpen = Boolean(anchorEl);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileDrawerOpen((prev) => !prev);
    } else {
      setDrawerOpen((prev) => !prev);
    }
  };

  const handleMobileNavClick = () => {
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleUserMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate('/login');
  };

  // Unread message count for badge
  const { data: unreadData } = useQuery({
    queryKey: ['unreadCount'],
    queryFn: messages.getUnreadCount,
    refetchInterval: 30000,
  });
  const unreadCount = unreadData?.unread_count || 0;

  const currentWidth = drawerOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH;

  // Role booleans used only for sidebar logic below

  // On mobile, sidebar is always in "expanded" visual mode (shows labels)
  const sidebarExpanded = isMobile ? true : drawerOpen;

  /* ── RENDER ────────────────────────────────────────────────────────── */

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: COLORS.contentBg }}>
      {/* ── Sidebar / Drawer ─────────────────────────────────────────── */}
      {isMobile ? (
        // Mobile: temporary overlay drawer
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          ModalProps={{ keepMounted: true }} // Better mobile perf
          sx={{
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              border: 'none',
              backgroundColor: COLORS.sidebarBg,
            },
          }}
        >
          <SidebarContent
            sidebarExpanded={sidebarExpanded}
            isMobile={isMobile}
            currentWidth={currentWidth}
            onDrawerToggle={handleDrawerToggle}
            onMobileNavClick={handleMobileNavClick}
          />
        </Drawer>
      ) : (
        // Desktop: fixed sidebar (permanent)
        <Box
          component="nav"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            width: currentWidth,
            zIndex: 1200,
            transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden',
          }}
        >
          <SidebarContent
            sidebarExpanded={sidebarExpanded}
            isMobile={isMobile}
            currentWidth={currentWidth}
            onDrawerToggle={handleDrawerToggle}
            onMobileNavClick={handleMobileNavClick}
          />
        </Box>
      )}

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          left: { xs: 0, md: currentWidth },
          right: 0,
          height: HEADER_HEIGHT,
          backgroundColor: COLORS.headerBg,
          borderBottom: `1px solid ${COLORS.headerBorder}`,
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 1.5, sm: 2, md: 3 },
          transition: 'left 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Left: Hamburger (mobile) + TopNavBar (Company > Functionality > Period) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.75 }, minWidth: 0, overflow: 'hidden' }}>
          {/* Hamburger menu button — mobile only */}
          {isMobile && (
            <IconButton
              onClick={handleDrawerToggle}
              edge="start"
              sx={{
                color: '#1E293B',
                mr: 0.5,
              }}
              aria-label="open navigation menu"
            >
              <MenuIcon />
            </IconButton>
          )}

          <TopNavBar />
        </Box>

        {/* Right: Divider + User */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexShrink: 0 }}>
          <Divider orientation="vertical" flexItem sx={{ my: 1.5, display: { xs: 'none', sm: 'block' } }} />

          <Tooltip title={user?.email ?? 'User'} arrow>
            <Box
              onClick={handleUserMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                p: 0.5,
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#F1F5F9' },
              }}
            >
              <Badge
                badgeContent={unreadCount}
                color="error"
                max={99}
                overlap="circular"
                sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 18, height: 18 } }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    backgroundColor: COLORS.accentPurple,
                    fontSize: 15,
                    fontWeight: 700,
                  }}
                >
                  {(user?.first_name ?? user?.email ?? 'U').charAt(0).toUpperCase()}
                </Avatar>
              </Badge>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B', lineHeight: 1.3 }}>
                  {user?.first_name && user?.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user?.email}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#94A3B8', lineHeight: 1.2 }}>
                  {user?.role === 'super_admin' ? 'SuperAdmin' : user?.role === 'admin' ? 'Admin' : user?.role === 'consultant_user' ? 'Consultant' : 'Company User'}
                </Typography>
              </Box>
            </Box>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={userMenuOpen}
            onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  minWidth: 200,
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  border: `1px solid ${COLORS.headerBorder}`,
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                {user?.first_name && user?.last_name
                  ? `${user.first_name} ${user.last_name}`
                  : user?.email}
              </Typography>
              <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block' }}>
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => { handleUserMenuClose(); navigate('/profile'); }}
              sx={{ py: 1.25, px: 2, fontSize: 14 }}
            >
              <ListItemIcon><PersonIcon sx={{ fontSize: 18 }} /></ListItemIcon>
              <ListItemText>My Profile</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => { handleUserMenuClose(); navigate('/messages'); }}
              sx={{ py: 1.25, px: 2, fontSize: 14 }}
            >
              <ListItemIcon>
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  <ChatIcon sx={{ fontSize: 18 }} />
                </Badge>
              </ListItemIcon>
              <ListItemText>Messages</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={handleLogout}
              sx={{
                py: 1.25,
                px: 2,
                gap: 1.5,
                fontSize: 14,
                color: '#EF4444',
                '&:hover': { backgroundColor: '#FEF2F2' },
              }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* ── Main Content + Footer wrapper ─────────────────────────────── */}
      <Box
        sx={{
          marginLeft: { xs: 0, md: `${currentWidth}px` },
          marginTop: `${HEADER_HEIGHT}px`,
          minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
          backgroundColor: COLORS.contentBg,
          transition: 'margin-left 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          width: { xs: '100%', md: `calc(100% - ${currentWidth}px)` },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          component="main"
          sx={{
            flex: 1,
            p: { xs: 1.5, sm: 2, md: 3 },
            maxWidth: 1536,
            mx: 'auto',
            width: '100%',
          }}
        >
          <Outlet />
        </Box>

        {/* ── Global keyboard shortcuts overlay (Ctrl+/ or ? to toggle) */}
        <KeyboardShortcutsOverlay />

        {/* ── First-time user walkthrough */}
        <WalkthroughOverlay />

        {/* ── Footer ──────────────────────────────────────────────────── */}
        <Box
          component="footer"
          sx={{
            minHeight: FOOTER_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            borderTop: `1px solid ${COLORS.headerBorder}`,
            backgroundColor: COLORS.headerBg,
            px: { xs: 1.5, sm: 3 },
            py: 1,
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 10, sm: 12 },
              color: '#94A3B8',
              textAlign: 'center',
            }}
          >
            terra17engine.com is distributed exclusively by{' '}
            <a
              href="https://kenbright.co.ke/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#94A3B8', textDecoration: 'underline' }}
            >
              Kenbright Actuarial &amp; Financial Services Limited
            </a>
            .
          </Typography>

          <Box
            component="img"
            src="/favicon.png"
            alt="Terra17"
            sx={{
              position: 'absolute',
              right: { xs: 12, sm: 24 },
              height: { xs: 20, sm: 28 },
              objectFit: 'contain',
              display: { xs: 'none', sm: 'block' },
              opacity: 0.5,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
